import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, getIdTokenResult, User } from "firebase/auth";
import { auth, getAuthOrThrow, getDbOrThrow } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";

type Role = "owner" | "admin" | "coach" | "member" | "viewer";
type Status = "loading" | "signed-out" | "signed-in";

export interface EnterpriseContext {
  status: Status;
  user: User | null;
  // Realm/org from custom claims when present; falls back to Firestore membership
  realmId: string | null;
  orgId: string | null;
  role: Role | null;
  // Whether user is confirmed as an org member (either claims or Firestore doc)
  isOrgMember: boolean;
  // Helper to re-check token/claims
  refresh: () => Promise<void>;
}

const CLAIM_KEYS = {
  realmId: "realmId",
  orgId: "orgId",
  role: "role",
};

async function fetchMembershipFromFirestore(uid: string, realmIdGuess?: string, orgIdGuess?: string) {
  const firestore = getDbOrThrow();
  // If we know both from guess, check that first:
  if (realmIdGuess && orgIdGuess) {
    const memberRef = doc(firestore, `realms/${realmIdGuess}/orgs/${orgIdGuess}/members/${uid}`);
    const snap = await getDoc(memberRef);
    if (snap.exists()) {
      const d = snap.data() as any;
      return { realmId: realmIdGuess, orgId: orgIdGuess, role: (d.role as Role) ?? "member", ok: true };
    }
  }
  // Otherwise, try common defaults; adjust as needed:
  const defaults = [
    ["enterprise", "synktribe-core"],
    ["gov", "gov-synktribe"],
    ["health", "pilot-hospital"],
  ];
  for (const [realm, org] of defaults) {
    const ref = doc(firestore, `realms/${realm}/orgs/${org}/members/${uid}`);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data() as any;
      return { realmId: realm, orgId: org, role: (d.role as Role) ?? "member", ok: true };
    }
  }
  return { realmId: null, orgId: null, role: null, ok: false };
}

export function useEnterpriseAuth(): EnterpriseContext {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [realmId, setRealmId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [isMember, setIsMember] = useState(false);

  const refresh = async () => {
    if (!auth?.currentUser) return;
    const currentAuth = getAuthOrThrow();
    const currentUser = currentAuth.currentUser;
    if (!currentUser) return;
    const token = await getIdTokenResult(currentUser, true);
    const claims = token.claims as Record<string, any>;
    const cRealm = (claims[CLAIM_KEYS.realmId] as string) || null;
    const cOrg = (claims[CLAIM_KEYS.orgId] as string) || null;
    const cRole = (claims[CLAIM_KEYS.role] as Role) || null;

    if (cRealm && cOrg) {
      setRealmId(cRealm);
      setOrgId(cOrg);
      setRole(cRole ?? "member");
      setIsMember(true);
      return;
    }

    // Fallback: check Firestore membership
    const fb = await fetchMembershipFromFirestore(currentUser.uid, cRealm ?? undefined, cOrg ?? undefined);
    setRealmId(fb.realmId);
    setOrgId(fb.orgId);
    setRole((fb.role as Role) ?? null);
    setIsMember(!!fb.ok);
  };

  useEffect(() => {
    if (!auth) {
      setStatus("signed-out");
      return undefined;
    }

    const unsub = onAuthStateChanged(getAuthOrThrow(), async (u) => {
      setUser(u);
      if (!u) {
        setStatus("signed-out");
        setRealmId(null);
        setOrgId(null);
        setRole(null);
        setIsMember(false);
        return;
      }
      setStatus("signed-in");
      await refresh();
    });
    return () => unsub();
  }, []);

  return useMemo(
    () => ({
      status,
      user,
      realmId,
      orgId,
      role,
      isOrgMember: isMember,
      refresh,
    }),
    [status, user, realmId, orgId, role, isMember]
  );
}
